// src/components/Instituicoes/EditarPerfilForm.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types'; 
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  TextField,
  Grid,
  Alert
} from '@mui/material';

// ⚡ CONSTANTES EXPANDIDAS
// Grupos de permissões organizados
const GRUPOS_PERMISSOES = {
  'Gestão': [
    'gerenciar_campanhas',
    'gerenciar_preinscricoes',
    'gerenciar_planos_pagamento',
    'acessar_relatorios'
  ],
  'Financeiro': [
    'gerenciar_planos_pagamento',
    'acessar_relatorios_financeiros',
    'vincular_estudantes_planos'
  ],
  'Acadêmico': [
    'gerenciar_cursos',
    'gerenciar_classes',
    'gerenciar_periodos',
    'gerenciar_disciplinas',
    'gerenciar_preinscricoes'
  ],
  'Conteúdo': [
    'gerenciar_conteudo',
    'gerenciar_sliders',
    'gerenciar_destaques'
  ]
};

// ⚡ NOVOS TIPOS DE PERFIL EXPANDIDOS
const TIPOS_PERFIL = [
  // Tipos administrativos existentes
  { value: 'ADMIN_INSTITUICAO', label: 'Admin Instituição' },
  { value: 'GESTAO', label: 'Gestão' },
  { value: 'FINANCEIRO', label: 'Financeiro' },
  { value: 'ACADEMICO', label: 'Acadêmico' },
  
  // ⚡ NOVOS TIPOS ACADÊMICOS
  { value: 'supervisor', label: 'Supervisor', isAcademico: true },
  { value: 'presidente', label: 'Presidente', isAcademico: true },
  { value: 'arguente', label: 'Arguente', isAcademico: true },
  { value: 'revisor_projeto', label: 'Revisor de Projeto', isAcademico: true },
  { value: 'direcao_cientifica', label: 'Direção Científica', isAcademico: true },
  { value: 'direcao_academica', label: 'Direção Acadêmica', isAcademico: true },
  { value: 'chefe_departamento', label: 'Chefe de Departamento', isAcademico: true }
];

// ⚡ CONSTANTES PARA CAMPOS ESPECÍFICOS
const AREAS_ESPECIALIZACAO = [
  'Ciências Exatas',
  'Ciências Humanas', 
  'Ciências Biológicas',
  'Engenharia',
  'Medicina',
  'Direito',
  'Economia',
  'Educação',
  'Tecnologia da Informação',
  'Administração'
];

const DEPARTAMENTOS = [
  'Matemática',
  'Física',
  'Química',
  'Biologia',
  'História',
  'Geografia',
  'Literatura',
  'Filosofia',
  'Engenharia Civil',
  'Engenharia Informática',
  'Medicina',
  'Direito',
  'Economia',
  'Administração'
];

const NIVEIS_ACADEMICOS = [
  { value: 'GRADUACAO', label: 'Graduação' },
  { value: 'ESPECIALIZACAO', label: 'Especialização' },
  { value: 'MESTRADO', label: 'Mestrado' },
  { value: 'DOUTORADO', label: 'Doutorado' },
  { value: 'POS_DOUTORADO', label: 'Pós-Doutorado' }
];

const EditarPerfilForm = ({ perfil, open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    tipo: '',
    status: '',
    permissoes: [],
    // ⚡ NOVOS CAMPOS PARA PERFIS ACADÊMICOS
    nome: '',
    username: '',
    email: '',
    areaEspecializacao: '',
    departamento: '',
    areaAtuacao: '',
    nivelAcademico: '',
    experienciaPresidencia: ''
  });
  const [errors, setErrors] = useState({});

  // ⚡ FUNÇÃO PARA IDENTIFICAR PERFIS ACADÊMICOS
  const isPerfilAcademico = (tipo) => {
    return TIPOS_PERFIL.find(t => t.value === tipo)?.isAcademico || false;
  };

  useEffect(() => {
    if (perfil) {
      setFormData({
        tipo: perfil.tipo || '',
        status: perfil.status || '',
        permissoes: perfil.permissoes || [],
        // ⚡ DADOS DO USUÁRIO ASSOCIADO (para perfis acadêmicos)
        nome: perfil.Usuario?.nome || '',
        username: perfil.Usuario?.username || '',
        email: perfil.Usuario?.email || '',
        areaEspecializacao: perfil.Usuario?.areaEspecializacao || '',
        departamento: perfil.Usuario?.departamento || '',
        areaAtuacao: perfil.Usuario?.areaAtuacao || '',
        nivelAcademico: perfil.Usuario?.nivelAcademico || '',
        experienciaPresidencia: perfil.Usuario?.experienciaPresidencia || ''
      });
    }
  }, [perfil]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissaoChange = (permissao) => {
    setFormData(prev => {
      const novasPermissoes = prev.permissoes.includes(permissao)
        ? prev.permissoes.filter(p => p !== permissao)
        : [...prev.permissoes, permissao];

      return {
        ...prev,
        permissoes: novasPermissoes
      };
    });
  };

  // Adicionar todas as permissões de um grupo
  const handleGrupoPermissaoChange = (grupo) => {
    const permissoesGrupo = GRUPOS_PERMISSOES[grupo];
    
    setFormData(prev => {
      const todasPermissoesGrupoPresentes = permissoesGrupo.every(p => 
        prev.permissoes.includes(p)
      );

      const novasPermissoes = todasPermissoesGrupoPresentes
        ? prev.permissoes.filter(p => !permissoesGrupo.includes(p))
        : [...new Set([...prev.permissoes, ...permissoesGrupo])];

      return {
        ...prev,
        permissoes: novasPermissoes
      };
    });
  };

  // ⚡ FUNÇÃO PARA RENDERIZAR CAMPOS ESPECÍFICOS POR TIPO
  const renderCamposEspecificos = () => {
    if (!isPerfilAcademico(formData.tipo)) return null;

    switch (formData.tipo) {
      case 'supervisor':
      case 'arguente':
      case 'revisor_projeto':
        return (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Área de Especialização</InputLabel>
              <Select
                name="areaEspecializacao"
                value={formData.areaEspecializacao}
                onChange={handleChange}
                label="Área de Especialização"
              >
                {AREAS_ESPECIALIZACAO.map(area => (
                  <MenuItem key={area} value={area}>{area}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'presidente':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                  label="Departamento"
                >
                  {DEPARTAMENTOS.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experiência em Presidência"
                name="experienciaPresidencia"
                value={formData.experienciaPresidencia}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </>
        );

      case 'chefe_departamento':
        return (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Departamento</InputLabel>
              <Select
                name="departamento"
                value={formData.departamento}
                onChange={handleChange}
                label="Departamento"
              >
                {DEPARTAMENTOS.map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'direcao_cientifica':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                  label="Departamento"
                >
                  {DEPARTAMENTOS.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Área de Atuação</InputLabel>
                <Select
                  name="areaAtuacao"
                  value={formData.areaAtuacao}
                  onChange={handleChange}
                  label="Área de Atuação"
                >
                  {AREAS_ESPECIALIZACAO.map(area => (
                    <MenuItem key={area} value={area}>{area}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );

      case 'direcao_academica':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                  label="Departamento"
                >
                  {DEPARTAMENTOS.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Nível Acadêmico</InputLabel>
                <Select
                  name="nivelAcademico"
                  value={formData.nivelAcademico}
                  onChange={handleChange}
                  label="Nível Acadêmico"
                >
                  {NIVEIS_ACADEMICOS.map(nivel => (
                    <MenuItem key={nivel.value} value={nivel.value}>
                      {nivel.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.tipo) newErrors.tipo = 'Tipo é obrigatório';
    if (!formData.status) newErrors.status = 'Status é obrigatório';

    // ⚡ VALIDAÇÕES ESPECÍFICAS PARA PERFIS ACADÊMICOS
    if (isPerfilAcademico(formData.tipo)) {
      if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
      if (!formData.username) newErrors.username = 'Username é obrigatório';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      id: perfil.id,
      ...formData
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Editar Perfil - {perfil?.Usuario?.nome}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* ⚡ ALERTA PARA PERFIS ACADÊMICOS */}
          {isPerfilAcademico(formData.tipo) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Este é um perfil acadêmico. Algumas informações são gerenciadas diretamente no usuário associado.
            </Alert>
          )}

          {/* Informações básicas */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Informações Básicas
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth error={!!errors.tipo}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  label="Tipo"
                >
                  {TIPOS_PERFIL.map(tipo => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                      {tipo.isAcademico && ' (Acadêmico)'}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tipo && <FormHelperText>{errors.tipo}</FormHelperText>}
              </FormControl>

              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="ATIVO">Ativo</MenuItem>
                  <MenuItem value="INATIVO">Inativo</MenuItem>
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Box>
          </Box>

          {/* ⚡ INFORMAÇÕES DO USUÁRIO (PARA PERFIS ACADÊMICOS) */}
          {isPerfilAcademico(formData.tipo) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Informações do Usuário
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Nome Completo"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      error={!!errors.nome}
                      helperText={errors.nome}
                      disabled // Nome não deve ser editável aqui
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      error={!!errors.username}
                      helperText={errors.username}
                      disabled // Username não deve ser editável aqui
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled // Email não deve ser editável aqui
                    />
                  </Grid>
                  
                  {/* ⚡ CAMPOS ESPECÍFICOS POR TIPO */}
                  {renderCamposEspecificos()}
                </Grid>
              </Box>
            </>
          )}

          {/* ⚡ PERMISSÕES (APENAS PARA PERFIS NÃO ACADÊMICOS) */}
          {!isPerfilAcademico(formData.tipo) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Permissões
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {Object.entries(GRUPOS_PERMISSOES).map(([grupo, permissoes]) => (
                    <Box key={grupo} sx={{ minWidth: '250px' }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={permissoes.every(p => formData.permissoes.includes(p))}
                              indeterminate={
                                permissoes.some(p => formData.permissoes.includes(p)) &&
                                !permissoes.every(p => formData.permissoes.includes(p))
                              }
                              onChange={() => handleGrupoPermissaoChange(grupo)}
                            />
                          }
                          label={<Typography variant="subtitle2">{grupo}</Typography>}
                        />
                        <Box sx={{ ml: 3 }}>
                          {permissoes.map(permissao => (
                            <FormControlLabel
                              key={permissao}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={formData.permissoes.includes(permissao)}
                                  onChange={() => handlePermissaoChange(permissao)}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {permissao.replace(/_/g, ' ').toLowerCase()}
                                </Typography>
                              }
                            />
                          ))}
                        </Box>
                      </FormGroup>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ⚡ PROPTYPES EXPANDIDAS
EditarPerfilForm.propTypes = {
  perfil: PropTypes.shape({
    id: PropTypes.string,
    Usuario: PropTypes.shape({
      nome: PropTypes.string,
      username: PropTypes.string,
      email: PropTypes.string,
      areaEspecializacao: PropTypes.string,
      departamento: PropTypes.string,
      areaAtuacao: PropTypes.string,
      nivelAcademico: PropTypes.string,
      experienciaPresidencia: PropTypes.string
    }),
    tipo: PropTypes.string,
    status: PropTypes.string,
    permissoes: PropTypes.arrayOf(PropTypes.string)
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default EditarPerfilForm;